import React from "react";

import { Link, NavLink, useParams } from "react-router-dom";

import { Checkbox } from "@components/inputs/shared";
import { WrappedLink } from "@components/navigation";
import WrappedNavLink from "@components/navigation/WrappedNavLink";
import { SportInteractionTypeEnum } from "@data-types/analytics-tracker/SportInteractionTypeEnum";
import TopTournamentMenuIcons from "@features/offer/shared/icon/TopTournamentMenuIcons";
import { updateUrlParams } from "@state";
import PrematchOfferMenuStore from "@state/stores/offer/prematch/PrematchOfferMenuStore";
import { analyticsTracker } from "@utils/analytics";
import { slugify } from "@utils/common/slugify";
import classNames from "classnames";
import { observer } from "mobx-react";

import { CategoryItemIcon } from ".";

export const CategoryItemButton = observer(function CategoryItemButton(props: {
	category: PrematchOfferMenuStore["menu"];
	store: {
		toggleUpdateProcessing?: PrematchOfferMenuStore["toggleUpdateProcessing"];
		type: "PrematchOfferMenuStore" | "LiveOfferMenuStore";
	};
}) {
	const { culture, period } = useParams<{
		culture: string;
		period: string;
	}>();

	const sportSlug =
		slugify(props.category.parent?.node.abrv) ||
		props.category.parent?.node.abrv ||
		"";
	const categorySlug =
		slugify(props.category.node.abrv) || props.category.node.abrv || "";
	const url = `/${culture}/sports/${period}/f/${sportSlug}/${categorySlug}`;

	const labelId = `sport-${props.category.parent?.node.id}-category-${props.category.node.id}`;
	const onClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (props.store.toggleUpdateProcessing != null) {
			props.store.toggleUpdateProcessing();
		}
		if (props.store.type === "PrematchOfferMenuStore") {
			updateUrlParams({ page: 1 }, true);
		}

		props.category.onToggleCheck();
		if (props.category.checkState === 1) {
			analyticsTracker.trackSportInteraction(
				window.location.pathname.includes("live")
					? SportInteractionTypeEnum.LiveCategory
					: SportInteractionTypeEnum.PrematchCategory,
				`${props.category.parent?.node.abrv}-${props.category.node.abrv}`
			);
		}
		window.scrollTo(0, 0);
	};
	const isLive = props.store.type === "LiveOfferMenuStore";
	const className = classNames("nav--tertiary__link", {
		"is-expanded": props.category.isExpanded,
		"is-active": props.category.checkState === 1,
	});
	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		props.category.toggleExpanded();
		if (
			props.category.node.isTopTournament &&
			props.category.children.length == 0
		) {
			if (!isLive) e.preventDefault();
			onClick(e);
		}
	};

	if (isLive) {
		return (
			<button type="button" className={className} onClick={handleClick}>
				<span className="nav--tertiary__title">
					<span className="input--check input--check--sml">
						<Checkbox
							id={labelId}
							className="input--check__box input--check--sml__box"
							name={props.category.node.name}
							title={props.category.node.name}
							checked={props.category.checkState === 1}
							indeterminate={props.category.checkState === 2}
							onClick={onClick}
							onChange={() => {}}
						/>
						<label
							htmlFor={labelId}
							className="input--check__label input--check--sml__label"
						/>
					</span>

					{props.category.node.isSpecialOffer ? (
						<TopTournamentMenuIcons
							sportAbrv={props.category.node.abrv || ""}
							tournamentAbrv={props.category.node.sportAbrv || ""}
							isSpecialOffer
							className="nav--tertiary__icon u-icon u-icon--base"
							imgClassName="nav--tertiary__icon__inner u-icon--base"
						/>
					) : (
						<CategoryItemIcon
							category={props.category}
							key={props.category.node.id}
						/>
					)}
					<span>{props.category.node.name}</span>
				</span>
				<span className="nav--tertiary__counter counter counter--base">
					{props.category.node.count}
				</span>
			</button>
		);
	}

	return (
		<WrappedLink
			to={url}
			className={className}
			onClick={(e) => {
				e.preventDefault();
				handleClick(e);
			}}
		>
			<span className="nav--tertiary__title">
				<span className="input--check input--check--sml">
					<Checkbox
						id={labelId}
						className="input--check__box input--check--sml__box"
						name={props.category.node.name}
						title={props.category.node.name}
						checked={props.category.checkState === 1}
						indeterminate={props.category.checkState === 2}
						onClick={onClick}
						onChange={() => {}}
					/>
					<label
						htmlFor={labelId}
						className="input--check__label input--check--sml__label"
					/>
				</span>

				{props.category.node.isSpecialOffer ? (
					<TopTournamentMenuIcons
						sportAbrv={props.category.node.abrv || ""}
						tournamentAbrv={props.category.node.sportAbrv || ""}
						isSpecialOffer
						className="nav--tertiary__icon u-icon u-icon--base"
						imgClassName="nav--tertiary__icon__inner u-icon--base"
					/>
				) : (
					<CategoryItemIcon
						category={props.category}
						key={props.category.node.id}
					/>
				)}
				<span>{props.category.node.name}</span>
			</span>
			<span className="nav--tertiary__counter counter counter--base">
				{props.category.node.count}
			</span>
		</WrappedLink>
	);
});
